import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Banknote,
  CalendarCheck,
  ChevronRight,
  FileText,
  HandCoins,
  Plus,
  ReceiptText,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useCompliance, type ComplianceItem } from "@/api/compliance";
import { useInvoices } from "@/api/invoices";
import { usePaymentRegister } from "@/api/payments";
import { useDashboard, useProfit, type DashboardReport, type MoneyTotal } from "@/api/reports";
import { useMe } from "@/api/users";
import { AttentionList, attentionItems, plural } from "@/components/dashboard/attention";
import {
  MoneyFlowChart,
  MoneyFlowLegend,
  MoneyFlowTable,
} from "@/components/dashboard/MoneyFlowChart";
import { InvoiceStatusBadges } from "@/components/invoices/InvoiceStatusBadges";
import { marginText, profitLabel } from "@/components/reports/ProfitPanel";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { buttonClass } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatTile } from "@/components/ui/stat-tile";
import { fyCode, todayIso } from "@/lib/dates";
import { formatDate, formatInr, formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";
import { cn } from "@/lib/utils";

const RECENT_ROWS = 5;
const DAY_MS = 86_400_000;

/** The first day of the month `count` months before `iso`: the trend's twelve months. */
function monthsBack(iso: string, count: number): string {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7)) - 1 - count;
  return new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10);
}

function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function longDate(now: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(now);
}

/** One amount per line: "USD 1,800.00" over "SAR 5,250.00". */
function Amounts({ totals, empty }: { totals: MoneyTotal[]; empty: string }) {
  if (totals.length === 0) return <>{empty}</>;
  return (
    <>
      {totals.map((total) => (
        <span key={total.currency} className="block">
          {formatMoney(total.amount, total.currency)}
        </span>
      ))}
    </>
  );
}

/** What is owed in each currency, with the overdue part of it in red. */
function ReceivablesBars({ data }: { data: DashboardReport }) {
  if (data.outstanding.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5" aria-hidden>
      {data.outstanding.map((total) => {
        const owed = Number(total.amount);
        const overdue = Number(
          data.overdue.find((item) => item.currency === total.currency)?.amount ?? 0,
        );
        const share = owed > 0 ? Math.min(100, (overdue / owed) * 100) : 0;
        return (
          <span key={total.currency} className="flex h-1.5 gap-[2px] overflow-hidden rounded-full">
            {share < 100 && <span className="flex-1 rounded-full bg-primary" />}
            {share > 0 && (
              <span className="rounded-full bg-destructive" style={{ width: `${share}%` }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

function Kpis({ data }: { data: DashboardReport }) {
  const lastMonth = data.months[data.months.length - 2];
  const loss = Number(data.profit.profit_inr) < 0;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        to="/invoices?status=unpaid"
        label="Receivables"
        icon={FileText}
        value={<Amounts totals={data.outstanding} empty="Nothing owed" />}
        hint={
          data.overdue_invoices > 0
            ? `${formatTotals(data.overdue)} overdue on ${plural(data.overdue_invoices, "invoice")}`
            : `${plural(data.unpaid_invoices, "unpaid invoice")}, none overdue`
        }
      >
        <ReceivablesBars data={data} />
      </StatTile>
      <StatTile
        to="/payments"
        label="Received this month"
        icon={Wallet}
        value={formatInr(data.received_this_month_inr)}
        hint={lastMonth ? `${formatInr(lastMonth.received_inr)} in ${lastMonth.label}` : undefined}
      />
      <StatTile
        to="/money?tab=payouts"
        label="Payouts owed"
        icon={HandCoins}
        value={<Amounts totals={data.profit.owed} empty="None owed" />}
        hint="Clients have paid; the payee has not been paid yet"
      />
      <StatTile
        to="/reports?report=profit"
        label={`${profitLabel(data.profit)}, FY ${fyCode(data.as_of)}`}
        icon={TrendingUp}
        tone={loss ? "negative" : "default"}
        value={formatInr(data.profit.profit_inr)}
        hint={`${marginText(data.profit)} on ${formatInr(data.profit.income_inr)} received`}
      />
    </div>
  );
}

function Section({
  title,
  description,
  link,
  className,
  children,
}: {
  title: string;
  description?: string;
  link?: { to: string; label: string };
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card className={cn("flex min-w-0 flex-col overflow-hidden", className)}>
      <div className="flex items-start justify-between gap-3 border-b px-4 py-3.5 md:px-5">
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-snug">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {link && (
          <Link
            to={link.to}
            className="inline-flex shrink-0 items-center gap-0.5 rounded text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {link.label}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        )}
      </div>
      {children}
    </Card>
  );
}

function Placeholder({ className }: { className?: string }) {
  return <div className={cn("rounded-lg bg-muted motion-safe:animate-pulse", className)} />;
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="px-4 py-8 text-center text-sm text-muted-foreground md:px-5">{children}</p>;
}

function MoneyFlow({ from, to }: { from: string; to: string }) {
  const profit = useProfit({ from, to });
  return (
    <Section
      title="Money in and out"
      description="The last twelve months, in rupees"
      link={{ to: "/reports?report=profit", label: "Profit report" }}
      className="xl:col-span-2"
    >
      <div className="flex flex-1 flex-col gap-4 p-4 md:p-5">
        {profit.isPending ? (
          <Placeholder className="h-64" />
        ) : profit.isError ? (
          <RequestError error={profit.error} />
        ) : (
          <>
            <MoneyFlowLegend months={profit.data.months} />
            <MoneyFlowChart months={profit.data.months} />
            <details className="group text-sm">
              <summary className="cursor-pointer select-none font-medium text-primary hover:underline">
                Show the figures
              </summary>
              <div className="mt-3">
                <MoneyFlowTable months={profit.data.months} />
              </div>
            </details>
          </>
        )}
      </div>
    </Section>
  );
}

function RecentInvoices() {
  const invoices = useInvoices({ limit: RECENT_ROWS });
  return (
    <Section
      title="Recent invoices"
      link={{ to: "/invoices", label: "All invoices" }}
      className="xl:col-span-2"
    >
      {invoices.isPending ? (
        <div className="p-4 md:p-5">
          <Placeholder className="h-40" />
        </div>
      ) : invoices.isError ? (
        <div className="p-4">
          <RequestError error={invoices.error} />
        </div>
      ) : invoices.data.length === 0 ? (
        <Quiet>No invoices yet. Create a draft for a vessel call to start.</Quiet>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th className="hidden sm:table-cell">Date</th>
                <th className="text-right">Amount</th>
                <th className="hidden md:table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.data.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="min-w-0">
                    <Link
                      to={`/invoices/${invoice.id}`}
                      className="whitespace-nowrap font-semibold text-primary hover:underline"
                    >
                      {invoice.invoice_no ?? "Draft"}
                    </Link>
                    <span className="block truncate text-xs text-muted-foreground">
                      {invoice.client_code}
                      {invoice.vessel_name ? `, ${invoice.vessel_name}` : ""}
                    </span>
                  </td>
                  <td className="hidden whitespace-nowrap sm:table-cell">
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {formatMoney(invoice.total, invoice.currency)}
                    {invoice.state === "issued" && invoice.balance !== invoice.total && (
                      <span className="block text-xs text-muted-foreground">
                        {Number(invoice.balance) > 0
                          ? `${formatMoney(invoice.balance, invoice.currency)} due`
                          : "Settled"}
                      </span>
                    )}
                  </td>
                  <td className="hidden md:table-cell">
                    <InvoiceStatusBadges invoice={invoice} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function RecentPayments() {
  const payments = usePaymentRegister({});
  const rows = payments.data?.slice(0, RECENT_ROWS) ?? [];
  return (
    <Section
      title="Recent payments"
      link={{ to: "/payments", label: "All payments" }}
      className="xl:col-span-2"
    >
      {payments.isPending ? (
        <div className="p-4 md:p-5">
          <Placeholder className="h-40" />
        </div>
      ) : payments.isError ? (
        <div className="p-4">
          <RequestError error={payments.error} />
        </div>
      ) : rows.length === 0 ? (
        <Quiet>No payments yet. Record one from the invoice it settles.</Quiet>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="hidden sm:table-cell">Received</th>
                <th>Invoice</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((payment) => (
                <tr key={payment.id}>
                  <td className="hidden whitespace-nowrap sm:table-cell">
                    {formatDate(payment.received_on)}
                  </td>
                  <td>
                    <Link
                      to={`/invoices/${payment.invoice_id}`}
                      className="whitespace-nowrap font-semibold text-primary hover:underline"
                    >
                      {payment.invoice_no ?? "Draft"}
                    </Link>
                    <span className="block text-xs text-muted-foreground">
                      {payment.client_code}
                      <span className="sm:hidden">, {formatDate(payment.received_on)}</span>
                    </span>
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {formatMoney(payment.amount, payment.currency)}
                    <span className="block text-xs text-muted-foreground">
                      {payment.inr_credited
                        ? formatInr(payment.inr_credited)
                        : "No rupee figure yet"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function dueBadge(item: ComplianceItem, today: string) {
  if (item.status === "overdue") {
    return <Badge variant="destructive">{plural(item.days_late, "day")} late</Badge>;
  }
  const days = Math.round((Date.parse(item.due_on) - Date.parse(today)) / DAY_MS);
  if (days === 0) return <Badge variant="warning">Due today</Badge>;
  return (
    <Badge variant={item.status === "due_soon" ? "warning" : "muted"}>
      In {plural(days, "day")}
    </Badge>
  );
}

function UpcomingCompliance({ today }: { today: string }) {
  const compliance = useCompliance();
  const open = (compliance.data ?? []).filter((item) => item.status !== "done").slice(0, RECENT_ROWS);
  return (
    <Section title="Compliance calendar" link={{ to: "/compliance", label: "Calendar" }}>
      {compliance.isPending ? (
        <div className="p-4 md:p-5">
          <Placeholder className="h-40" />
        </div>
      ) : compliance.isError ? (
        <div className="p-4">
          <RequestError error={compliance.error} />
        </div>
      ) : open.length === 0 ? (
        <Quiet>Nothing open. Every filing on the calendar is done.</Quiet>
      ) : (
        <ul className="divide-y">
          {open.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-4 py-3 md:px-5">
              <CalendarCheck className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">
                  {item.form} {item.period_label}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {item.name}, due {formatDate(item.due_on)}
                </span>
              </span>
              {dueBadge(item, today)}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const GST_BAR = {
  ok: "bg-primary",
  watch: "bg-warning",
  high: "bg-warning",
  exceeded: "bg-destructive",
} as const;

function Position({ data }: { data: DashboardReport }) {
  const gst = data.gst;
  return (
    <Section title="Position today">
      <dl className="divide-y">
        <div className="flex items-start justify-between gap-4 px-4 py-3 md:px-5">
          <dt className="text-sm text-muted-foreground">
            <Link to="/money" className="hover:text-primary hover:underline">
              Bank and cash
            </Link>
          </dt>
          <dd className="text-right text-sm font-semibold tabular-nums">
            <Amounts totals={data.cash} empty="Not placed yet" />
          </dd>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 px-4 py-3 md:px-5">
          <dt className="text-sm text-muted-foreground">
            <Link to="/reports?report=gst" className="hover:text-primary hover:underline">
              GST threshold, FY {gst.fy_code}
            </Link>
          </dt>
          <dd className="text-right text-sm font-semibold tabular-nums">{gst.percentage}%</dd>
          <dd className="col-span-2 flex flex-col gap-2">
            <div
              role="meter"
              aria-label="Turnover against the GST threshold"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(100, gst.percentage)}
              className="h-1.5 overflow-hidden rounded-full bg-muted"
            >
              <div
                className={cn("h-full rounded-full", GST_BAR[gst.level])}
                style={{ width: `${Math.min(100, gst.percentage)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {formatInr(gst.turnover_inr)} of {formatInr(gst.threshold_inr)}
            </p>
          </dd>
        </div>
        <div className="flex items-start justify-between gap-4 px-4 py-3 md:px-5">
          <dt className="text-sm text-muted-foreground">
            {data.mail.configured ? (
              <Link to="/mail?tab=decide" className="hover:text-primary hover:underline">
                Mail to decide
              </Link>
            ) : (
              "Mail to decide"
            )}
          </dt>
          <dd className="text-sm font-semibold">
            {data.mail.configured ? data.mail.pending : "Not set up"}
          </dd>
        </div>
        <div className="flex items-start justify-between gap-4 px-4 py-3 md:px-5">
          <dt className="text-sm text-muted-foreground">
            <Link to="/expenses?status=unpaid" className="hover:text-primary hover:underline">
              Unpaid bills
            </Link>
          </dt>
          <dd className="text-sm font-semibold tabular-nums">
            {data.bills.unpaid > 0 ? formatInr(data.bills.unpaid_inr) : "None"}
          </dd>
        </div>
      </dl>
    </Section>
  );
}

const ACTIONS = [
  { to: "/invoices?status=unpaid", label: "Record payment", icon: Wallet },
  { to: "/expenses", label: "Add expense", icon: Banknote },
  { to: "/receipts/new", label: "New receipt", icon: ReceiptText },
];

export function DashboardPage() {
  const me = useMe();
  const dashboard = useDashboard();
  const data = dashboard.data;
  const now = new Date();
  const today = todayIso(now);
  const firstName = me.data?.full_name.trim().split(/\s+/)[0];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{longDate(now)}</p>
          <h1 className="mt-1 text-[1.75rem] font-bold leading-tight md:text-[2rem]">
            {greeting(now)}
            {firstName ? `, ${firstName}` : ""}
          </h1>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <Link to="/invoices/new" className={cn(buttonClass(), "sm:order-last")}>
            <Plus className="h-4 w-4" aria-hidden />
            New invoice
          </Link>
          {ACTIONS.map((action) => (
            <Link key={action.to} to={action.to} className={buttonClass("outline")}>
              <action.icon className="h-4 w-4 text-brand-600" aria-hidden />
              {action.label}
            </Link>
          ))}
        </div>
      </header>

      {dashboard.isError && <RequestError error={dashboard.error} />}

      {dashboard.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((key) => (
            <Placeholder key={key} className="h-32" />
          ))}
        </div>
      ) : (
        data && <Kpis data={data} />
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        <MoneyFlow from={monthsBack(today, 11)} to={today} />
        <Section
          title="Needs attention"
          description={data ? undefined : "Loading…"}
          className="order-first xl:order-none xl:max-h-[34rem]"
        >
          {data && (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <AttentionList items={attentionItems(data)} />
            </div>
          )}
        </Section>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <RecentInvoices />
        <UpcomingCompliance today={today} />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <RecentPayments />
        {data && <Position data={data} />}
      </div>
    </div>
  );
}
